import {useAgencyQuery} from "@/api/agencyApi";
import {useRequestPermissionUpgradeMutation} from "@/api/companyApi";
import EditIcon from "@/assets/colorable/edit.svg?react";
import PendingIcon from "@/assets/colorable/pending.svg?react";
import TimesIcon from "@/assets/colorable/times.svg?react";
import TrashIcon from "@/assets/colorable/trash.svg?react";
import WarningIcon from "@/assets/colorable/warning.svg?react";
import UserAvatarPlaceholder from "@/assets/images/user_avatar_placeholder.svg";
import Button from "@/components/Common/Button";
import {Tooltip} from "@/components/Common/Tooltip";
import ChangeAccountOwnershipModal from "@/components/MultiAccount/ChangeAccountOwnershipModal";
import ChangeAgencyOwnershipModal from "@/components/MultiAccount/ChangeAgencyOwnershipModal";
import InviteMember from "@/components/MultiAccount/InviteMember";
import PermissionSelect from "@/components/MultiAccount/PermissionSelect";
import RemoveCompanyMemberModal from "@/components/MultiAccount/RemoveCompanyMemberModal";
import UpdateInvitePermission from "@/components/MultiAccount/UpdateInvitePermission";
import UpdateUserPermission from "@/components/MultiAccount/UpdateUserPermission";
import UserNotificationToggle from "@/components/MultiAccount/UserNotificationToggle";
import type {SubBrandInviteSerializer} from "@/generated/DjangoTypes";
import {useIsAdmin, useUserId, useUserPermission} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import {AbilityContext, Can} from "@/permissions";
import {useAgencyStore, useSettingsAgencyStore} from "@/store/agency";
import {settingsModalAtom} from "@/store/jotaiAtoms";
import type {TAccountMember, TPermission} from "@/store/types";
import {getUserPermission} from "@/utils/permissionUtils";
import {useSetAtom} from "jotai";
import {useContext, useState, type FC} from "react";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";

const CompanyMembers: FC<{
  companyId: number;
  companyMembers: TAccountMember[];
  companyName: string;
  companyInvites: SubBrandInviteSerializer[];
  upgradePermission(memberId: number): void;
  className?: string;
  hideInviteHeadings?: boolean;
  isAgency?: boolean;
  isLargeBrand?: boolean;
  isSubbrand?: boolean;
}> = ({
  companyId,
  companyMembers,
  companyName,
  companyInvites,
  upgradePermission,
  className,
  hideInviteHeadings = false,
  isAgency = false,
  isLargeBrand = false,
  isSubbrand = false,
}) => {
  const {t} = useTranslation(["multi_account", "common"]);
  const abilityContext = useContext(AbilityContext);
  const setSettingsModal = useSetAtom(settingsModalAtom);
  const isAdmin = useIsAdmin();
  const loggedInUserId = useUserId();
  const isCompanyOwner = useUserPermission({companyId}) === "ADMIN";
  const canDeleteMembers = abilityContext.can("delete", "CompanyMember");
  const agencyStore = useAgencyStore(state => state.agency);
  const settingsAgency = useSettingsAgencyStore(state => state.agency);
  const storedAgency =
    settingsAgency.id === companyId ? settingsAgency : agencyStore.id === companyId ? agencyStore : null;
  const {data: queriedAgency} = useAgencyQuery({
    agencyId: companyId,
    queryOptions: {enabled: isAgency && !storedAgency},
  });
  const agency = queriedAgency ?? storedAgency;
  const agencyOwnerId = agency?.agency_owner_id;
  const viewerIsAgencyOwner = loggedInUserId === agencyOwnerId;
  const canChangeAgencyOwnership = !!agency?.multiple_brands && (isAdmin || viewerIsAgencyOwner);
  const [agencyMemberToRemove, setAgencyMemberToRemove] = useState<TAccountMember | null>(null);
  const [agencyMemberToTransferOwnershipTo, setAgencyMemberToTransferOwnershipTo] = useState<TAccountMember | null>(
    null,
  );
  const [companyMemberToRemove, setCompanyMemberToRemove] = useState<TAccountMember | null>(null);
  const [companyMemberToTransferOwnershipTo, setCompanyMemberToTransferOwnershipTo] = useState<TAccountMember | null>(
    null,
  );
  const [isPermissionFormShown, setPermissionFormShown] = useState(false);
  const [permissionToRequest, setPermissionToRequest] = useState<TPermission | null>(null);

  const closePermissionForm = () => {
    setPermissionToRequest(null);
    setPermissionFormShown(false);
  };

  const {mutate: requestPermissionUpgrade, isPending: isRequestingPermission} = useRequestPermissionUpgradeMutation({
    companyId,
    onSuccessCallback: closePermissionForm,
  });

  return (
    <div className={clsxm("flex flex-col gap-5", className)}>
      {!!agencyMemberToRemove && (
        <RemoveCompanyMemberModal
          companyId={companyId}
          companyName={companyName}
          companyMember={agencyMemberToRemove}
          isAgency
          onClose={() => setAgencyMemberToRemove(null)}
        />
      )}
      {!!agencyMemberToTransferOwnershipTo && (
        <ChangeAgencyOwnershipModal
          agencyId={companyId}
          agencyMember={agencyMemberToTransferOwnershipTo}
          onClose={() => setAgencyMemberToTransferOwnershipTo(null)}
        />
      )}
      {!!companyMemberToRemove && (
        <RemoveCompanyMemberModal
          companyId={companyId}
          companyName={companyName}
          companyMember={companyMemberToRemove}
          onClose={() => setCompanyMemberToRemove(null)}
        />
      )}
      {!!companyMemberToTransferOwnershipTo && (
        <ChangeAccountOwnershipModal
          companyId={companyId}
          companyMember={companyMemberToTransferOwnershipTo}
          onClose={() => setCompanyMemberToTransferOwnershipTo(null)}
        />
      )}
      {!isSubbrand && (
        <Can I="create" a="CompanyInvite">
          <InviteMember
            agencyId={isAgency ? companyId : null}
            companyId={isAgency ? null : companyId}
            hideHeadings={hideInviteHeadings}
          />
        </Can>
      )}
      {companyMembers?.map(member => {
        const isViewingSelf = member.user.id === loggedInUserId;
        const canUpdateMemberList = abilityContext.can("update", "CompanyMembers");
        const canUpdateMember = abilityContext.can("update", "CompanyMember");
        const hasPermissionRequest = member.permission_request?.status === "PENDING";
        const shouldShowPermissionRequestWarning = hasPermissionRequest && (isViewingSelf || canUpdateMember);
        const isCanonicalAgencyOwner = member.user.id === agencyOwnerId;
        const memberPermission = getUserPermission(member);
        const isCompanyMemberOwner = memberPermission === "ADMIN";
        const isBrandReadOnlyRow = isSubbrand || (!canUpdateMemberList && !isViewingSelf && !isAdmin);
        const canMakeCompanyMemberOwner =
          !isCompanyMemberOwner && !isViewingSelf && (isCompanyOwner || isAdmin) && !isBrandReadOnlyRow;
        const canShowCompanyMemberTrash =
          !isBrandReadOnlyRow && (isAdmin || (!isCompanyMemberOwner && (isViewingSelf || canDeleteMembers)));

        return (
          <div key={member.id} className="flex flex-col gap-2">
            <div key={member.id} className="flex items-center gap-2 max-md:flex-wrap md:justify-between">
              <div className="flex flex-wrap items-center gap-3">
                <div className="border-neutral-grey-light-dark flex items-center gap-2 rounded-full border p-1 pr-2">
                  <div className="border-neutral-grey-light-dark size-6 shrink-0 rounded-full border">
                    <img
                      src={member.user.profile_picture ?? UserAvatarPlaceholder}
                      className="h-full w-full shrink-0 rounded-full"
                    />
                  </div>
                  <span
                    onClick={() => {
                      navigator.clipboard.writeText(member.user.email!);
                      toast.success(t("email_copied"));
                    }}
                    data-tip
                    data-tooltip-id={`member-${member.id}-email`}
                    className="cursor-copy bg-linear-(--primary-dark-gradient) bg-clip-text font-semibold text-transparent">
                    {member.user.first_name} {member.user.last_name}
                  </span>
                  <Tooltip id={`member-${member.id}-email`} place="top" variant="dark" delayShow={100}>
                    {t("email")}: {member.user.email}
                  </Tooltip>
                </div>
                {!isAgency && isViewingSelf && !hasPermissionRequest && !isPermissionFormShown && (
                  <Can not I="create" a="Campaign">
                    <Button
                      buttonStyle="Blue"
                      className="hidden h-auto px-4 py-1 sm:flex"
                      onClick={() => setPermissionFormShown(true)}>
                      {t("request_more_permissions")}
                    </Button>
                  </Can>
                )}
              </div>
              {isAgency && (
                <div className="ml-auto flex items-center gap-3">
                  {isCanonicalAgencyOwner && (
                    <div className="justify-end text-xs font-medium">{t("admin_permissions")}</div>
                  )}
                  {!isCanonicalAgencyOwner && (
                    <PermissionSelect
                      value="ADMIN"
                      excludedPermissions={["PURCHASE", "READ", "WRITE"]}
                      topOptions={[
                        {value: "ADMIN", label: t("common:admin")},
                        ...(canChangeAgencyOwnership ? [{value: "MAKE_OWNER", label: t("make_owner")}] : []),
                      ]}
                      onChange={value => {
                        if (value === "MAKE_OWNER") {
                          setAgencyMemberToTransferOwnershipTo(member);
                        }
                      }}
                      containerClassName="max-w-46.25"
                    />
                  )}
                  {(isAdmin || (!isCanonicalAgencyOwner && !isViewingSelf)) && (
                    <Can I="delete" a="CompanyMember">
                      <button
                        aria-label={t("remove_user")}
                        data-tooltip-id={`agency-member-${member.id}-remove`}
                        data-tip
                        onClick={() => setAgencyMemberToRemove(member)}
                        className="text-neutral-grey-dark hover:text-danger-dark-red border-neutral-grey-light-dark flex size-8 items-center justify-center rounded-md border">
                        <TrashIcon className="size-3.5 shrink-0" />
                      </button>
                      <Tooltip id={`agency-member-${member.id}-remove`} place="top" variant="dark" delayShow={100}>
                        {t("remove_user")}
                      </Tooltip>
                    </Can>
                  )}
                </div>
              )}
              {!isAgency && (
                <>
                  <div className="ml-auto flex items-center gap-3 max-md:order-3 max-md:ml-0 max-md:basis-full">
                    {isViewingSelf && (
                      <Button
                        buttonStyle="White"
                        className="h-auto rounded-full"
                        onClick={() =>
                          setSettingsModal({scope: {id: companyId, type: "brand"}, section: "account_profile"})
                        }>
                        <EditIcon className="size-3 shrink-0" />
                      </Button>
                    )}
                    {isCompanyMemberOwner || isBrandReadOnlyRow ? (
                      <div className="flex items-center gap-3">
                        <div className="border-primary-light-solid text-neutral-black h-8 rounded-md border px-4 py-2 text-xs">
                          {t(`${memberPermission.toLowerCase() as "write"}_permissions`)}
                        </div>
                        {isCompanyMemberOwner && canShowCompanyMemberTrash && (
                          <>
                            <button
                              aria-label={t(isViewingSelf ? "leave" : "remove_user")}
                              data-tooltip-id={`company-member-${member.id}-remove`}
                              data-tip
                              onClick={() => setCompanyMemberToRemove(member)}
                              className="text-neutral-grey-dark hover:text-danger-dark-red border-neutral-grey-light-dark flex size-8 items-center justify-center rounded-md border">
                              <TrashIcon className="size-3.5 shrink-0" />
                            </button>
                            <Tooltip
                              id={`company-member-${member.id}-remove`}
                              place="top"
                              variant="dark"
                              delayShow={100}>
                              {t(isViewingSelf ? "leave" : "remove_user")}
                            </Tooltip>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        {shouldShowPermissionRequestWarning && (
                          <div className="border-warning-sand bg-warning-light-yellow text-warning-dark-yellow hidden min-h-8 items-center gap-4 rounded-md border px-2 py-1 text-sm font-semibold lg:flex">
                            <div className="flex items-center gap-2">
                              <WarningIcon className="text-warning-medium-yellow size-3.5 shrink-0" />
                              <span className="inline-block max-w-50">
                                {t("requesting_permission", {
                                  permission: t(
                                    `${member.permission_request?.permission_type?.toLowerCase() as "write"}_permissions`,
                                  ),
                                })}
                              </span>
                            </div>
                            <Can I="update" a="CompanyMember">
                              <button
                                onClick={() => upgradePermission(member.user.id)}
                                className="text-neutral-grey-dark hover:text-neutral-grey-light">
                                <TimesIcon className="size-2 shrink-0" />
                              </button>
                            </Can>
                          </div>
                        )}
                        <UpdateUserPermission
                          companyId={companyId}
                          companyMember={member}
                          permission={memberPermission}
                          onMakeOwner={
                            canMakeCompanyMemberOwner ? () => setCompanyMemberToTransferOwnershipTo(member) : undefined
                          }
                        />
                        {canShowCompanyMemberTrash && (
                          <>
                            <button
                              aria-label={t(isViewingSelf ? "leave" : "remove_user")}
                              data-tooltip-id={`company-member-${member.id}-remove`}
                              data-tip
                              onClick={() => setCompanyMemberToRemove(member)}
                              className="text-neutral-grey-dark hover:text-danger-dark-red border-neutral-grey-light-dark flex size-8 items-center justify-center rounded-md border">
                              <TrashIcon className="size-3.5 shrink-0" />
                            </button>
                            <Tooltip
                              id={`company-member-${member.id}-remove`}
                              place="top"
                              variant="dark"
                              delayShow={100}>
                              {t(isViewingSelf ? "leave" : "remove_user")}
                            </Tooltip>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="max-md:order-2 max-md:ml-auto">
                    <UserNotificationToggle
                      companyId={companyId}
                      isInitiallyEnabled={member.user.email_notification_preferences?.email_enabled ?? true}
                      userId={member.user.id}
                    />
                  </div>
                </>
              )}
            </div>
            {!isAgency && (
              <div className="flex items-center gap-3 lg:hidden">
                {isViewingSelf && !hasPermissionRequest && !isPermissionFormShown && (
                  <Can not I="create" a="Campaign">
                    <Button
                      buttonStyle="Blue"
                      className="h-auto px-4 py-1 sm:hidden"
                      onClick={() => setPermissionFormShown(true)}>
                      {t("request_more_permissions")}
                    </Button>
                  </Can>
                )}
                {shouldShowPermissionRequestWarning && (
                  <div className="border-warning-sand bg-warning-light-yellow text-warning-dark-yellow flex min-h-8 items-center gap-4 rounded-md border px-2 py-1 text-sm font-semibold">
                    <div className="flex items-center gap-2">
                      <WarningIcon className="text-warning-medium-yellow size-3.5 shrink-0" />
                      {t("requesting_permission", {
                        permission: t(
                          `${member.permission_request?.permission_type?.toLowerCase() as "write"}_permissions`,
                        ),
                      })}
                    </div>
                    <Can I="update" a="CompanyMember">
                      <button
                        onClick={() => upgradePermission(member.user.id)}
                        className="text-neutral-grey-dark hover:text-neutral-grey-light">
                        <TimesIcon className="size-2 shrink-0" />
                      </button>
                    </Can>
                  </div>
                )}
              </div>
            )}
            {isViewingSelf && isPermissionFormShown && (
              <div className="flex flex-wrap items-end justify-between gap-3">
                <PermissionSelect
                  containerClassName="w-80 min-w-0 md:max-w-full"
                  dropDownIconVariant="FILLED"
                  excludedPermissions={
                    getUserPermission(member) === "READ" ? ["READ"] : (["READ", "PURCHASE"] as TPermission[])
                  }
                  onChange={value => setPermissionToRequest(value as TPermission)}
                  placeholder={t("permission_select_placeholder")}
                  renderButtonClassName="border-neutral-grey-light-dark h-8.5 rounded-lg px-3 [&>span]:text-sm [&>svg]:mr-2.5 [&>svg]:size-2.5"
                  value={permissionToRequest}
                />
                <div className="flex items-center gap-3">
                  <Button buttonStyle="White" className="h-10 px-5 text-sm" onClick={closePermissionForm}>
                    {t("cancel")}
                  </Button>
                  <Button
                    buttonStyle="Gradient"
                    className="h-10 px-5 text-sm"
                    isDisabled={!permissionToRequest || isRequestingPermission}
                    isLoading={isRequestingPermission}
                    onClick={() => requestPermissionUpgrade({newPermission: permissionToRequest!})}>
                    {t("request")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        );
      })}
      {companyInvites.map(invite => (
        <div
          key={invite.id}
          data-testid="pending-invite-row"
          className="flex items-center justify-between gap-2 sm:gap-3">
          <div className="border-neutral-grey-light-dark flex max-w-[45vw] min-w-20 items-center gap-2 rounded-full border border-dashed p-1 pr-2 sm:max-w-[auto]">
            <div className="border-neutral-grey-light-dark size-6 shrink-0 rounded-full border">
              <img src={UserAvatarPlaceholder} className="h-full w-full shrink-0 rounded-full" />
            </div>
            <span
              onClick={() => {
                navigator.clipboard.writeText(invite.email!);
                toast.success(t("email_copied"));
              }}
              data-tip
              data-tooltip-id={`invite-${invite.id}-email`}
              className="cursor-copy truncate bg-linear-(--primary-dark-gradient) bg-clip-text font-semibold break-all text-transparent">
              {invite.email}
            </span>
            <Tooltip id={`invite-${invite.id}-email`} place="top" variant="dark" delayShow={100}>
              {t("pending_invitation_to")}: <br />
              {invite.email}
            </Tooltip>
          </div>
          <div className="ml-auto flex items-center gap-1.5 sm:gap-3">
            <div className="border-primary-light-solid flex items-center gap-2 rounded-md border p-2">
              <PendingIcon className="text-neutral-grey-dark" />
              <span className="text-neutral-grey-dark hidden text-xs sm:inline">{t("pending")}</span>
            </div>
            <Can I="update" a="CompanyInvite">
              <UpdateInvitePermission
                companyId={companyId}
                invite={invite}
                isAgency={isAgency}
                isLargeBrand={isLargeBrand}
              />
            </Can>
            <Can not I="update" a="CompanyInvite">
              <div className="border-primary-light-solid text-neutral-black h-8 rounded-md border px-4 py-2 text-xs text-nowrap">
                {t(`${invite.permission!.toLowerCase() as "write"}_permissions`)}
              </div>
            </Can>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CompanyMembers;
